import React, { useEffect, useRef, useState } from "react";
import * as shaka from "shaka-player/dist/shaka-player.compiled";
import { PlayCircle, VideoOff } from "lucide-react";

let polyfillsInstalled = false;
function ensurePolyfills() {
  if (polyfillsInstalled || !shaka?.polyfill) return;
  try {
    shaka.polyfill.installAll();
    polyfillsInstalled = true;
  } catch (_) {
    /* noop */
  }
}

// Tiles loop (`<video loop>`) so plain trim_out reached-natural-EOF wouldn't
// happen soon enough — this makes the tile loop within [trimIn, trimOut]
// instead of the browser's native 0→end loop, on any playback path.
function attachTrimLoop(video, trimIn, trimOut) {
  if (trimIn == null && trimOut == null) return () => {};
  const inS = trimIn ?? 0;
  const onTimeUpdate = () => {
    if (trimOut != null && video.currentTime >= trimOut - 0.05) {
      video.currentTime = inS;
    }
  };
  const onEnded = () => {
    video.currentTime = inS;
    video.play().catch(() => { /* autoplay blocked */ });
  };
  video.addEventListener("timeupdate", onTimeUpdate);
  video.addEventListener("ended", onEnded);
  return () => {
    video.removeEventListener("timeupdate", onTimeUpdate);
    video.removeEventListener("ended", onEnded);
  };
}

/**
 * Universal HLS/DASH tile:
 *   1. If the browser supports native HLS (Safari, iOS), plays via `<video src>`.
 *   2. Otherwise uses Shaka Player with MSE (Chrome, Firefox, Edge, Android).
 *   3. Falls back to a stylized brand placeholder if no URL, unsupported, or a
 *      network/codec error occurs (e.g. Chromium builds without H.264).
 */
const ShakaVideoTile = React.forwardRef(function ShakaVideoTile(
  { url, brandName, brandColor, muted = true, volume = 1, testId, poster, trimIn = null, trimOut = null },
  externalRef
) {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [state, setState] = useState(url ? "loading" : "empty");

  // Expose the underlying <video> element to the parent for Web Audio hooks.
  React.useImperativeHandle(externalRef, () => videoRef.current);

  // Sync muted + volume props to the underlying <video> element without
  // reinitialising the player.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
    v.volume = Math.max(0, Math.min(1, volume));
  }, [muted, volume]);

  useEffect(() => {
    ensurePolyfills();

    const video = videoRef.current;
    if (!url || !video) {
      setState(!url ? "empty" : "error");
      return;
    }

    let cancelled = false;
    // Set CORS mode BEFORE assigning any src. Web Audio API taints the
    // MediaElementAudioSourceNode when the media resource is cross-origin
    // without a proper CORS handshake — Chrome silently outputs silence
    // through the graph in that case. Public HLS test origins (mux.dev,
    // unified-streaming, apple) all serve CORS-clean segments, so this
    // annotation is enough to unlock audio.
    try { video.crossOrigin = "anonymous"; } catch { /* noop */ }

    // 0. Progressive MP4/MOV/WebM path (e.g. served directly from R2). Shaka's
    // MSE pipeline is unnecessary here — the browser plays these natively.
    const stripped = (url.split("#")[0].split("?")[0] || "").toLowerCase();
    const isProgressive = /\.(mp4|mov|m4v|webm)$/.test(stripped);
    if (isProgressive) {
      let retriedNoCors = false;
      let detachTrim = () => {};
      const start = () => {
        video.src = url;
        video.muted = muted;
        video.play().catch(() => { /* autoplay blocked */ });
      };
      const onCanPlay = () => {
        if (cancelled) return;
        setState("playing");
        if (trimIn != null) video.currentTime = trimIn;
        detachTrim = attachTrimLoop(video, trimIn, trimOut);
      };
      const onError = () => {
        if (cancelled) return;
        if (!retriedNoCors) {
          // If the object's origin doesn't send CORS headers, the crossOrigin
          // fetch aborts. Retry once WITHOUT crossOrigin so the video still
          // plays (audio-meter graph is disabled for that tile only) instead
          // of showing "preview offline".
          retriedNoCors = true;
          try { video.removeAttribute("crossorigin"); video.crossOrigin = null; } catch { /* noop */ }
          try { video.load(); } catch { /* noop */ }
          start();
          return;
        }
        setState("error");
      };
      video.addEventListener("canplay", onCanPlay);
      video.addEventListener("error", onError);
      start();
      return () => {
        cancelled = true;
        video.removeEventListener("canplay", onCanPlay);
        video.removeEventListener("error", onError);
        detachTrim();
        try { video.pause(); } catch (_) { /* noop */ }
        video.removeAttribute("src");
        try { video.load(); } catch (_) { /* noop */ }
      };
    }

    const nativeHls =
      video.canPlayType("application/vnd.apple.mpegurl") ||
      video.canPlayType("application/x-mpegURL");

    // 1. Native HLS path (Safari, iOS)
    if (nativeHls) {
      video.src = url;
      video.muted = muted;
      let detachTrim = () => {};
      const onCanPlay = () => {
        if (cancelled) return;
        setState("playing");
        if (trimIn != null) video.currentTime = trimIn;
        detachTrim = attachTrimLoop(video, trimIn, trimOut);
      };
      const onError = () => !cancelled && setState("error");
      video.addEventListener("canplay", onCanPlay);
      video.addEventListener("error", onError);
      video.play().catch(() => {
        /* autoplay blocked */
      });
      return () => {
        cancelled = true;
        video.removeEventListener("canplay", onCanPlay);
        video.removeEventListener("error", onError);
        detachTrim();
        try { video.pause(); } catch (_) { /* noop */ }
        video.removeAttribute("src");
        try { video.load(); } catch (_) { /* noop */ }
      };
    }

    // 2. MSE / Shaka path
    if (!shaka?.Player || !shaka.Player.isBrowserSupported()) {
      setState("error");
      return;
    }

    const player = new shaka.Player();
    playerRef.current = player;
    let detachTrim = () => {};

    (async () => {
      try {
        await player.attach(video);
        player.addEventListener("error", () => {
          if (!cancelled) setState("error");
        });
        await player.load(url, trimIn ?? undefined);
        if (!cancelled) {
          setState("playing");
          video.muted = muted;
          detachTrim = attachTrimLoop(video, trimIn, trimOut);
          try {
            await video.play();
          } catch (_) {
            /* autoplay blocked — user can click to play */
          }
        }
      } catch (_) {
        if (!cancelled) setState("error");
      }
    })();

    return () => {
      cancelled = true;
      detachTrim();
      try {
        player.destroy();
      } catch (_) {
        /* noop */
      }
      playerRef.current = null;
    };
    // We intentionally re-run when `url` OR the trim window changes — the
    // separate muted/volume effect above syncs those props to the underlying
    // <video> without rebuilding the Shaka player (rebuilding on every mute
    // toggle silences audio).
  }, [url, trimIn, trimOut]);

  const showFallback = state === "empty" || state === "error";

  return (
    <div className="absolute inset-0" data-testid={testId} data-shaka-state={state}>
      <video
        ref={videoRef}
        className={`w-full h-full object-cover bg-black transition-opacity duration-500 ${
          state === "playing" ? "opacity-100" : "opacity-0"
        }`}
        playsInline
        muted={muted}
        autoPlay
        loop
        poster={poster || undefined}
        crossOrigin="anonymous"
        data-testid={testId ? `${testId}-video` : undefined}
      />

      {showFallback && (
        <div className="absolute inset-0 flex items-center justify-center bg-black overflow-hidden">
          {poster ? (
            <img
              src={poster}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-40"
              data-testid={testId ? `${testId}-poster` : undefined}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="absolute inset-0 checkerboard" />
          )}
          <div
            className="font-display font-black text-6xl tracking-tighter opacity-90 relative z-10 drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]"
            style={{ color: brandColor || "#3B82F6" }}
          >
            {brandName}
          </div>
          {state === "error" && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-amber-500/80 z-10">
              <VideoOff className="w-3 h-3" strokeWidth={1.5} /> preview offline
            </div>
          )}
        </div>
      )}

      {state === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          {poster && (
            <img
              src={poster}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-55"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          <PlayCircle className="w-8 h-8 text-white/70 animate-pulse relative z-10 drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]" />
        </div>
      )}

      <div className="absolute inset-0 scan-line pointer-events-none" />
    </div>
  );
});

export default ShakaVideoTile;
